import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"

import Dialog from "@/components/dialog/Dialog"
import TextField from "@/components/form/textField/TextField"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import BasicButton from "@/components/button/BasicButton"
import { createGlobalStore } from "@/stores/global"
import { createSession, readSessionDataItems, createSessionDataBulk } from "@/app/api/session"
import { Session } from "@/types/session"

import "./copySessionDialog.scss"

type CopySessionDialogProps = {
  open: boolean
  onClose: () => void
  sourceSession: Session | null
  idfActivity: number
}

type SplitOption = "default" | "1m" | "5m" | "15m"

const SPLIT_DURATION_MS: Record<SplitOption, number | null> = {
  default: null,
  "1m": 60 * 1000,
  "5m": 5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
}

const SPLIT_ITEMS: { label: string; value: SplitOption }[] = [
  { label: "None", value: "default" },
  { label: "1 min", value: "1m" },
  { label: "5 min", value: "5m" },
  { label: "15 min", value: "15m" },
]

// 분할 시 마지막 자투리 조각이 이 길이 미만이면 세션을 만들지 않는다 (WEB-115)
const MIN_SPLIT_CHUNK_MS = 60 * 1000

export default function CopySessionDialog({
  open,
  onClose,
  sourceSession,
  idfActivity,
}: CopySessionDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [name, setName] = useState("")
  const [splitOption, setSplitOption] = useState<SplitOption>("default")
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (open && sourceSession) {
      setName(sourceSession.name)
      setSplitOption("default")
    }
  }, [open, sourceSession])

  const { setSnackbar } = createGlobalStore((state) => ({
    setSnackbar: state.setSnackBar,
  }))

  const handleClose = () => {
    setName("")
    setSplitOption("default")
    onClose()
  }

  // 선택한 분할 단위로 [start, finish] 구간을 chunk 배열로 분해
  const chunks = useMemo(() => {
    if (!sourceSession) {
      return []
    }
    const splitMs = SPLIT_DURATION_MS[splitOption]
    if (splitMs == null) {
      return []
    }

    const startMs = new Date(sourceSession.dateStart).valueOf()
    const finishMs = new Date(sourceSession.dateFinish).valueOf()
    const result: { dateStart: string; dateFinish: string }[] = []
    for (let s = startMs; s < finishMs; s += splitMs) {
      const e = Math.min(s + splitMs, finishMs)
      // 마지막 자투리 조각이 1분 미만이면 만들지 않는다 (예: 00:00~03:31 을 1분 분할 시 03:00~03:31 제외)
      if (e - s < MIN_SPLIT_CHUNK_MS) {
        break
      }
      result.push({
        dateStart: new Date(s).toISOString(),
        dateFinish: new Date(e).toISOString(),
      })
    }

    return result
  }, [sourceSession, splitOption])

  const handleSave = async () => {
    if (!sourceSession || isLoading) {
      return
    }

    const baseName = name || sourceSession.name
    setIsLoading(true)

    try {
      // 1. 원본 세션의 매칭된 플레이어 데이터 조회 (직접 호출 — 캐시 키 충돌 방지)
      const sourceData = await readSessionDataItems(sourceSession.idfSession)
      const matchedData = sourceData?.response?.filter((data) => data.matchedToSession) ?? []

      // 2. 분할 모드면 chunk별로, 아니면 단일 세션으로 생성 plan 작성
      const sessionPlans =
        splitOption === "default"
          ? [
              {
                name: baseName,
                dateStart: sourceSession.dateStart,
                dateFinish: sourceSession.dateFinish,
              },
            ]
          : chunks.map((chunk, i) => ({
              name: `${baseName} (${i + 1})`,
              dateStart: chunk.dateStart,
              dateFinish: chunk.dateFinish,
            }))

      // 3. 세션 생성 후 응답의 idfSession으로 곧바로 선수 일괄 복사
      //    (직접 API 호출 — mutation 훅을 쓰면 세션 생성 시점에 readSessionList가 invalidate되어
      //     선수 복사 전의 "미참여" 상태가 readSessionDataItems 캐시에 박히는 race가 생김)
      const createdIdfSessions: number[] = []
      for (const plan of sessionPlans) {
        const created = await createSession({
          idfActivity,
          idfSessionType: sourceSession.idfSessionType,
          name: plan.name,
          dateStart: plan.dateStart,
          dateFinish: plan.dateFinish,
          startToDate: plan.dateStart,
          finishToDate: plan.dateFinish,
          autoMatch: false,
        })
        const idfSession = created?.response
        if (typeof idfSession !== "number") {
          throw new Error("createSession 응답에 idfSession이 없음")
        }
        createdIdfSessions.push(idfSession)

        // 세션 구간과 참여 구간의 교집합이 있는 선수만, 구간 경계로 잘라서 복사
        // (서버 bulkMatchData는 교집합이 초 단위 절단 후 양수가 아니면 전체 요청을 400으로 거부)
        const planStartMs = new Date(plan.dateStart).valueOf()
        const planFinishMs = new Date(plan.dateFinish).valueOf()
        const sessionDataList = matchedData
          .map((data) => ({
            data,
            startMs: Math.max(new Date(data.dateStart).valueOf(), planStartMs),
            finishMs: Math.min(new Date(data.dateFinish).valueOf(), planFinishMs),
          }))
          .filter(
            ({ startMs, finishMs }) => Math.floor(finishMs / 1000) > Math.floor(startMs / 1000),
          )
          .map(({ data, startMs, finishMs }) => ({
            ...data,
            idfSession,
            dateStart: new Date(startMs).toISOString(),
            dateFinish: new Date(finishMs).toISOString(),
          }))

        if (sessionDataList.length > 0) {
          await createSessionDataBulk(idfSession, sessionDataList)
        }
      }

      // 4. 캐시 갱신 + 다이얼로그 닫기
      // readSessionDataItems는 새로 만든 세션만 targeted invalidate
      // (broad invalidate 시 분할 모드에서 N개 기존 쿼리가 동시 refetch되어 useQueries.combine의
      //  새 ref + page.tsx useEffect 조합이 setState 폭주를 일으킴)
      createdIdfSessions.forEach((idfSession) => {
        queryClient.invalidateQueries({ queryKey: ["readSessionDataItems", idfSession] })
      })
      queryClient.invalidateQueries({ queryKey: ["readSessionList"] })
      queryClient.invalidateQueries({ queryKey: ["readActivityParticipatedPlayerList"] })
      queryClient.invalidateQueries({ queryKey: ["readAnalyzingActivity"] })
      handleClose()

      setSnackbar({
        open: true,
        message: t("messages.successAdd"),
        severity: "success",
      })
    } catch {
      setSnackbar({
        open: true,
        message: t("messages.errorUpdate"),
        severity: "error",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("copySession")}
      maxWidth="600px"
      className="copy-session-dialog"
      footer={
        <div className="copy-session-dialog-footer">
          <BasicButton size="md" type="default-outlined" onClick={handleClose}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton size="md" type="primary-contained" onClick={handleSave} disabled={isLoading}>
            {t("button.copy", { defaultValue: "Copy" })}
          </BasicButton>
        </div>
      }>
      <div className="copy-session-dialog-content">
        <TextField label={t("name")} value={name} onChange={(e) => setName(e.target.value)} />
        <div className="copy-session-dialog-split">
          <label className="copy-session-dialog-split-label">
            {t("splitOptions", { defaultValue: "Split Options" })}
          </label>
          <RadioGroup
            items={SPLIT_ITEMS}
            direction="row"
            value={splitOption}
            onChange={(_, value) => setSplitOption((value as SplitOption) ?? "default")}
          />
          <p className="copy-session-dialog-split-hint">
            {`${t("totalSessions", { defaultValue: "Total sessions" })}: `}
            <strong>{splitOption === "default" ? 1 : chunks.length}</strong>
          </p>
        </div>
      </div>
    </Dialog>
  )
}
